import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/Context';
import { api } from '../../services/api.util';
import { fetchMockTestsBySubject } from '../../services/subjects.util';
import { getSubjectById } from '../../services/exams.util';
import VideoPlayer from '../../components/common/VideoPlayer';
import SimpleDocumentViewer from '../../components/common/SimpleDocumentViewer';
import PDFViewer from '../../components/common/PDFViewer';
import { API_URL } from '../../config/api.config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlay, 
  faCalendar, 
  faEye, 
  faBookmark, 
  faClipboardList,
  faBook,
  faVideo,
  faClock,
  faFileAlt,
  faFilePdf,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

const RENEWAL_MESSAGE = 'Your subscription has ended. Please renew it to continue accessing this content.';

const SubjectPage = () => {
  const { subjectId, materialCode, mockTestCode } = useParams();
  const navigate = useNavigate();
  const { loggedIn } = useAuthContext();
  const isMaterialContext = Boolean(materialCode);
  const isMockTestContext = Boolean(mockTestCode);
  const [activeTab, setActiveTab] = useState((isMaterialContext || isMockTestContext) ? 'pdfs' : 'videos');
  
  // Subject data state management
  const [subjectData, setSubjectData] = useState(null);
  const [subjectLoading, setSubjectLoading] = useState(true);
  const [subjectError, setSubjectError] = useState(null);
  
  // Video state management
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videosError, setVideosError] = useState(null);

  // Mock tests state management
  const [mockTests, setMockTests] = useState([]);
  const [mockTestsLoading, setMockTestsLoading] = useState(true);
  const [mockTestsError, setMockTestsError] = useState(null);

  // PDFs state management (replaces materials under subject)
  const [pdfs, setPdfs] = useState([]);
  const [pdfsLoading, setPdfsLoading] = useState(true);
  const [pdfsError, setPdfsError] = useState(null);

  // Material context: material data and purchase status when viewing subject under a material
  const [materialContextData, setMaterialContextData] = useState(null);
  const [materialPurchaseStatus, setMaterialPurchaseStatus] = useState(null);

  // Mock test context: package and purchase status when viewing subject under a mock test package
  const [mockTestContextData, setMockTestContextData] = useState(null);
  const [mockTestContextLoading, setMockTestContextLoading] = useState(true);
  const [mockTestContextError, setMockTestContextError] = useState(null);
  const [mockTestPurchaseStatus, setMockTestPurchaseStatus] = useState(null);

  // Access control state (exam context)
  const [accessInfo, setAccessInfo] = useState(null);

  // Video player state
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  // Document viewer state
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  // PDF viewer state (specifically for PDF files)
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentPDF, setCurrentPDF] = useState(null);

  // Authentication protection - redirect to login if not authenticated
  useEffect(() => {
    if (!loggedIn) {
      navigate('/login', { replace: true });
      return;
    }
  }, [loggedIn, navigate]);

  // Fetch subject data when component mounts or subjectId changes
  useEffect(() => {
    if (!loggedIn) return;
    
    const loadSubjectData = async () => {
      if (!subjectId) return;
      
      try {
        setSubjectLoading(true);
        setSubjectError(null);
        const data = await getSubjectById(subjectId);
        setSubjectData(data);
      } catch (error) {
        console.error('Error loading subject data:', error);
        setSubjectError(error.message || 'Failed to load subject data. Please try again.');
      } finally {
        setSubjectLoading(false);
      }
    };

    loadSubjectData();
  }, [subjectId, loggedIn]);

  // When in material context, load material and purchase status
  useEffect(() => {
    if (!loggedIn || !materialCode) return;
    
    const loadMaterialContext = async () => {
      try {
        const { getMaterialBySlug } = await import('../../services/materials.util');
        const { checkMaterialPurchase } = await import('../../services/materialsPurchase.util');
        const material = await getMaterialBySlug(materialCode);
        setMaterialContextData(material);
        const response = await checkMaterialPurchase(material._id);
        setMaterialPurchaseStatus(response.data);
      } catch (err) {
        console.error('Error loading material context:', err);
        setMaterialContextData(null);
        setMaterialPurchaseStatus({ hasPurchased: false });
      }
    };

    loadMaterialContext();
  }, [materialCode, loggedIn]);

  // When in mock test context, load mock test package and purchase status
  useEffect(() => {
    if (!loggedIn || !mockTestCode) {
      if (!mockTestCode) setMockTestContextLoading(false);
      return;
    }
    setMockTestContextLoading(true);
    setMockTestContextError(null);
    const loadMockTestContext = async () => {
      try {
        const { getMockTestBySlug } = await import('../../services/mockTests.util');
        const { checkMockTestPurchase } = await import('../../services/mockTestsPurchase.util');
        const pkg = await getMockTestBySlug(mockTestCode);
        setMockTestContextData(pkg);
        setMockTestContextError(null);
        const response = await checkMockTestPurchase(pkg._id);
        setMockTestPurchaseStatus(response.data);
      } catch (err) {
        console.error('Error loading mock test context:', err);
        setMockTestContextData(null);
        setMockTestContextError(err.message || 'Failed to load mock test package.');
        setMockTestPurchaseStatus({ hasPurchased: false });
      } finally {
        setMockTestContextLoading(false);
      }
    };

    loadMockTestContext();
  }, [mockTestCode, loggedIn]);

  // Fetch videos when component mounts or subjectId changes
  useEffect(() => {
    // Only load videos if user is logged in
    if (!loggedIn) return;
    
    const loadVideos = async () => {
      if (!subjectId) return;
      
      try {
        setVideosLoading(true);
        setVideosError(null);
        const data = await api.get(`/subjects/${subjectId}/videos`);
        
        if (data.success) {
          setVideos(data.data);
          // Update access info if provided
          if (data.accessInfo) {
            setAccessInfo(data.accessInfo);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch videos');
        }
      } catch (err) {
        console.error('Failed to load videos:', err);
        setVideosError(err.message);
        // Set fallback empty array
        setVideos([]);
      } finally {
        setVideosLoading(false);
      }
    };

    loadVideos();
  }, [subjectId, loggedIn]);

  // Fetch mock tests when component mounts or subjectId changes
  useEffect(() => {
    // Only load mock tests if user is logged in
    if (!loggedIn) return;
    
    const loadMockTests = async () => {
      if (!subjectId) return;
      
      try {
        setMockTestsLoading(true);
        setMockTestsError(null);
        console.log('Loading mock tests for subject:', subjectId);
        const mockTestsData = await fetchMockTestsBySubject(subjectId);
        console.log('Mock tests loaded:', {
          count: mockTestsData?.length || 0,
          data: mockTestsData
        });
        setMockTests(mockTestsData || []);
      } catch (err) {
        console.error('Failed to load mock tests:', err);
        setMockTestsError(err.message);
        // Set fallback empty array
        setMockTests([]);
      } finally {
        setMockTestsLoading(false);
      }
    };

    loadMockTests();
  }, [subjectId, loggedIn]);

  // Fetch PDFs when component mounts or subjectId changes (when mock test context, wait for package and filter by isMockTest + mockTest)
  useEffect(() => {
    if (!loggedIn) return;
    if (!subjectId) return;
    if (mockTestCode && mockTestContextLoading) return; // wait for mock test package when in mock test context
    if (mockTestCode && !mockTestContextData && mockTestContextError) {
      setPdfs([]);
      setPdfsError(mockTestContextError);
      setPdfsLoading(false);
      return;
    }
    
    const loadPdfs = async () => {
      try {
        setPdfsLoading(true);
        setPdfsError(null);
        
        // Build URL with query parameters for mock test context
        let url = `/subjects/${subjectId}/pdfs`;
        if (mockTestCode) {
          // Filter by isMockTest=true to show all mock test PDFs for this subject
          // Note: We don't filter by mockTest ID since PDFs don't have that field set
          url += `?isMockTest=true`;
          console.log('Loading PDFs for mock test context:', {
            mockTestCode,
            mockTestId: mockTestContextData?._id,
            mockTestTitle: mockTestContextData?.title,
            subjectId,
            url
          });
        }
        
        const data = await api.get(url);
        
        console.log('PDFs API response:', {
          success: data.success,
          count: data.data?.length || 0,
          url,
          pdfs: data.data?.map(p => ({ id: p._id, title: p.title, isMockTest: p.isMockTest, mockTest: p.mockTest }))
        });
        
        if (data.success) {
          setPdfs(data.data || []);
          if (data.accessInfo) {
            setAccessInfo(prev => prev || data.accessInfo);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch PDFs');
        }
      } catch (err) {
        console.error('Failed to load PDFs:', {
          error: err,
          message: err.message,
          subjectId,
          mockTestCode,
          mockTestContextData: mockTestContextData?._id
        });
        setPdfsError(err.message);
        setPdfs([]);
      } finally {
        setPdfsLoading(false);
      }
    };

    loadPdfs();
  }, [subjectId, loggedIn, mockTestCode, mockTestContextLoading, mockTestContextData, mockTestContextError]);

  // Video player handlers
  const handlePlayVideo = (video) => {
    // Allow access if video is marked as free, regardless of purchase status
    if (video.isFree) {
      setCurrentVideo(video);
      setShowVideoPlayer(true);
      return;
    }
    
    // Check if video is locked (tests-only access or no access)
    if (video.locked || video.restrictedContent === 'videos' || (accessInfo?.accessLevel?.accessType === 'tests')) {
      if (!accessInfo?.hasAccess) {
        alert(accessInfo?.isExpired ? (accessInfo.message || RENEWAL_MESSAGE) : 'Video access restricted. Please purchase the exam to access videos.');
      } else {
        alert('Video access restricted. You have tests-only access. Please upgrade to full access to view videos.');
      }
      return;
    }
    
    setCurrentVideo(video);
    setShowVideoPlayer(true);
  };

  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false);
    setCurrentVideo(null);
  };

  // Document viewer handlers
  const handleViewDocument = async (document, type = 'document') => {
    // PDF type (subject PDFs): check material or exam access
    // For mock test PDFs (isMockTest: true), check test access instead of material access
    if (type === 'pdf') {
      const isMockTestPdf = document.isMockTest === true;
      const hasMaterialAccess = materialPurchaseStatus?.hasPurchased || materialContextData?.isFree || !materialContextData?.isPurchasable;
      const hasMockTestAccess = mockTestPurchaseStatus?.hasPurchased || mockTestContextData?.isFree || !mockTestContextData?.isPurchasable;
      const hasExamAccess = accessInfo?.hasAccess && (isMockTestPdf ? accessInfo?.accessLevel?.canAccessTests : accessInfo?.accessLevel?.canAccessMaterials);
      const hasAccess = isMockTestContext ? hasMockTestAccess : (isMaterialContext ? hasMaterialAccess : hasExamAccess);
      if (!document.isFree && !hasAccess) {
        if (isMockTestContext) {
          alert(mockTestPurchaseStatus?.isExpired ? (mockTestPurchaseStatus.message || RENEWAL_MESSAGE) : 'Purchase this mock test package to access tests.');
        } else if (isMaterialContext) {
          alert(materialPurchaseStatus?.isExpired ? (materialPurchaseStatus.message || RENEWAL_MESSAGE) : 'Purchase this material to access PDFs.');
        } else if (isMockTestPdf) {
          alert(accessInfo?.isExpired ? (accessInfo.message || RENEWAL_MESSAGE) : 'Test access restricted. Please purchase the exam to access tests.');
        } else {
          alert(accessInfo?.isExpired ? (accessInfo.message || RENEWAL_MESSAGE) : 'Material access restricted. Please purchase the exam to access study materials.');
        }
        return;
      }
      try {
        const viewUrl = `${API_URL}/pdfs/${document._id}/view`;
        const response = await fetch(viewUrl, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Failed to get file URL: ${response.status}`);
        const data = await response.json();
        if (!data.success || !data.fileUrl) throw new Error('Invalid response format');
        setCurrentPDF({ url: data.fileUrl, title: document.title });
        setShowPDFViewer(true);
      } catch (error) {
        console.error('Error getting PDF URL:', error);
        window.open(`${API_URL}/pdfs/${document._id}/view`, '_blank');
      }
      return;
    }

    // Legacy Material type (exam materials)
    if (type === 'Material' && !document.isFree && (document.locked || document.restrictedContent === 'materials' ||
        !accessInfo?.hasAccess || !accessInfo?.accessLevel?.canAccessMaterials)) {
      if (!accessInfo?.hasAccess) {
        alert(accessInfo?.isExpired ? (accessInfo.message || RENEWAL_MESSAGE) : 'Material access restricted. Please purchase the exam to access study materials.');
      } else {
        alert('Material access restricted. You have tests-only access. Please upgrade to full access to view study materials.');
      }
      return;
    }

    if (type === 'Mock Test' && !document.isFree && (!accessInfo?.hasAccess || !accessInfo?.accessLevel?.canAccessTests)) {
      alert(accessInfo?.isExpired ? (accessInfo.message || RENEWAL_MESSAGE) : 'Test access restricted. Please purchase the exam to access tests.');
      return;
    }

    try {
      const response = await fetch(document.url, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Failed to get file URL: ${response.status}`);
      const data = await response.json();
      if (!data.success || !data.fileUrl) throw new Error('Invalid response format');
      const actualFileUrl = data.fileUrl;
      const contentType = data.contentType || '';
      const isPDF = contentType.includes('application/pdf');
      if (isPDF) {
        setCurrentPDF({ url: actualFileUrl, title: document.title });
        setShowPDFViewer(true);
      } else {
        setCurrentDocument({ url: actualFileUrl, title: document.title, type });
        setShowDocumentViewer(true);
      }
    } catch (error) {
      console.error('Error getting file URL:', error);
      window.open(document.url, '_blank');
    }
  };

  const handleCloseDocumentViewer = () => {
    setShowDocumentViewer(false);
    setCurrentDocument(null);
  };

  const handleClosePDFViewer = () => {
    setShowPDFViewer(false);
    setCurrentPDF(null);
  };

  // Don't render content if user is not logged in (prevents flash before redirect)
  if (!loggedIn) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Redirecting...</span>
                </div>
                <h5 className="text-white">Redirecting to login...</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Loading state for subject data
  if (subjectLoading) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-white">Loading subject data...</h5>
                <p className="text-light">Please wait while we fetch the subject information.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state for subject data
  if (subjectError) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <FontAwesomeIcon icon={faBook} size="3x" />
                </div>
                <h5 className="text-white">Error Loading Subject</h5>
                <p className="text-light mb-4">{subjectError}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No subject data state
  if (!subjectData) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-secondary mb-3">
                  <FontAwesomeIcon icon={faBook} size="3x" />
                </div>
                <h5 className="text-white">Subject Not Found</h5>
                <p className="text-light">The requested subject could not be found. Please check the URL and try again.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderVideosTab = () => {
    // Helper function to format duration from seconds to MM:SS
    const formatDuration = (durationSec) => {
      if (!durationSec) return "00:00";
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Helper function to check if video is new (created within last 7 days)
    const isVideoNew = (createdAt) => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(createdAt) > sevenDaysAgo;
    };

          // Generate a more robust placeholder that always works
      const generateRobustPlaceholder = (title, duration) => {
        const encodedTitle = encodeURIComponent(title.substring(0, 15));
        const encodedDuration = encodeURIComponent(duration || '00:00');
        return `https://placehold.co/600x400/4A90E2/FFFFFF?text=${encodedTitle}%0A${encodedDuration}`;
      };


    // Map API data to component data structure
    const videoData = videos.map((video, index) => {
      // Enhanced thumbnail handling with multiple fallback strategies
      let thumbnailUrl = video.thumbnail;
      
      // If thumbnail is empty or null, use fallback
      if (!thumbnailUrl || thumbnailUrl === '') {
        thumbnailUrl = "https://placehold.co/600x400?text=Video\\nThumbnail";
      }
      
      // If thumbnail is a relative path, construct full URL
      if (thumbnailUrl && !thumbnailUrl.startsWith('http')) {
        thumbnailUrl = `https://gvkedu.s3.amazonaws.com/${thumbnailUrl}`;
      }
      
      // If it's an S3 URL, ensure it has proper formatting
      if (thumbnailUrl && thumbnailUrl.includes('gvkedu.s3.amazonaws.com')) {
        // Remove any double slashes except for the protocol
        thumbnailUrl = thumbnailUrl.replace(/([^:])\/+/g, '$1/');
      }
      
      return {
        id: video._id,
        title: video.title,
        date: video.createdAt,
        duration: formatDuration(video.durationSec),
        thumbnail: thumbnailUrl,
        placeholderThumbnail: generateRobustPlaceholder(video.title, formatDuration(video.durationSec)), // Fallback thumbnail
        views: Math.floor(Math.random() * 3000) + 500, // Random views for demo
        isNew: isVideoNew(video.createdAt),
        description: video.description,
        url: video.url,
        order: video.order
      };
    });

    // Sort videos by order, then by title
    const sortedVideos = [...videoData].sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.title.localeCompare(b.title);
    });

    // Loading state
    if (videosLoading) {
      return (
        <div className={`tab-pane fade ${activeTab === 'videos' ? 'show active' : ''}`}>
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-white">Loading videos...</h5>
                <p className="text-light">Please wait while we fetch the latest video content.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (videosError) {
      return (
        <div className={`tab-pane fade ${activeTab === 'videos' ? 'show active' : ''}`}>
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <FontAwesomeIcon icon={faVideo} size="3x" />
                </div>
                <h5 className="text-white">Error Loading Videos</h5>
                <p className="text-light mb-4">{videosError}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No videos state
    if (sortedVideos.length === 0) {
      return (
        <div className={`tab-pane fade ${activeTab === 'videos' ? 'show active' : ''}`}>
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-secondary mb-3">
                  <FontAwesomeIcon icon={faVideo} size="3x" />
                </div>
                <h5 className="text-white">No Videos Available</h5>
                <p className="text-light">There are currently no video lectures available for this subject. Please check back later.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Calculate total duration for stats
    const totalDurationSec = videos.reduce((total, video) => total + (video.durationSec || 0), 0);
    const totalDurationFormatted = `${Math.floor(totalDurationSec / 3600)}h ${Math.floor((totalDurationSec % 3600) / 60)}m`;

    return (
      <div className={`tab-pane fade ${activeTab === 'videos' ? 'show active' : ''}`}>
        {/* Header Section */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="text-white mb-2">Video Lectures</h4>
                <p className="text-light mb-0">{sortedVideos.length} videos available • Learn at your own pace</p>
              </div>
              <div className="d-flex gap-3">
                <div className="text-center">
                  <div className="text-primary h5 mb-1">{sortedVideos.length}</div>
                  <small className="text-light">Total Videos</small>
                </div>
                <div className="text-center">
                  <div className="text-success h5 mb-1">{totalDurationFormatted}</div>
                  <small className="text-light">Total Duration</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Grid */}
        <div className="row g-4">
          {sortedVideos.map((video) => {
            const originalVideo = videos.find(v => v._id === video.id);
            // Video is locked unless it's free or user has proper access
            const isLocked = !originalVideo?.isFree && (originalVideo?.locked || originalVideo?.restrictedContent === 'videos' || (accessInfo?.accessLevel?.accessType === 'tests'));
            
            return (
              <div key={video.id} className="col-lg-4 col-md-6">
                <div className={`video-card-modern position-relative ${isLocked ? 'locked-content' : ''}`}>
                  <div className="video-thumbnail position-relative overflow-hidden">
                    <img 
                      src={video.thumbnail} 
                      className={`w-100 ${isLocked ? 'opacity-50' : ''}`}
                      alt={video.title}
                      style={{ height: '200px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = video.placeholderThumbnail;
                      }}
                      crossOrigin="anonymous"
                    />
                    <div className="video-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                      <button 
                        className={`play-button btn ${isLocked ? 'btn-secondary' : 'btn-light'} rounded-circle`}
                        onClick={() => {
                          if (originalVideo) handlePlayVideo(originalVideo);
                        }}
                        disabled={isLocked}
                      >
                        <FontAwesomeIcon icon={isLocked ? faClipboardList : faPlay} size="lg" />
                      </button>
                    </div>
                    {isLocked && (
                      <div className="position-absolute top-0 end-0 m-3">
                        <span className="badge bg-warning text-dark px-2 py-1">
                          <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                          Tests Only
                        </span>
                      </div>
                    )}
                    {originalVideo?.isFree && (
                      <div className="position-absolute top-0 start-0 m-3">
                        <span className="badge bg-gradient bg-info px-2 py-1">
                          <FontAwesomeIcon icon={faPlay} className="me-1" />
                          Free
                        </span>
                      </div>
                    )}
                    <div className="video-duration position-absolute bottom-0 end-0 m-3">
                      <span className="badge bg-dark bg-opacity-75 px-2 py-1">
                        {video.duration || '00:00'}
                      </span>
                    </div>
                    {video.isNew && !originalVideo?.isFree && (
                      <div className="position-absolute top-0 start-0 m-3">
                        <span className="badge bg-gradient bg-success px-2 py-1">New</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="video-content p-4">
                    <h6 className="text-white mb-3 line-height-relaxed">
                      {video.title}
                    </h6>
                    
                    <div className="video-meta d-flex justify-content-between align-items-center mb-4">
                      <div className="d-flex align-items-center text-light">
                        <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary opacity-75" size="sm" />
                        <small>{new Date(video.date).toLocaleDateString()}</small>
                      </div>
                      <div className="d-flex align-items-center text-light">
                        <FontAwesomeIcon icon={faEye} className="me-2 text-success opacity-75" size="sm" />
                        <small>{video.views.toLocaleString()}</small>
                      </div>
                    </div>
                    
                    <div className="video-actions d-flex gap-2">
                      <button 
                        className={`btn ${isLocked ? 'btn-secondary' : (originalVideo?.isFree ? 'btn-info' : 'btn-light')} flex-grow-1 py-2`}
                        onClick={() => {
                          if (originalVideo) handlePlayVideo(originalVideo);
                        }}
                        disabled={isLocked}
                      >
                        <FontAwesomeIcon icon={isLocked ? faClipboardList : faPlay} className="me-2" />
                        {isLocked ? 'Locked' : (originalVideo?.isFree ? 'Free to Watch' : 'Watch Now')}
                      </button>
                      <button className="btn btn-light px-3">
                        <FontAwesomeIcon icon={faBookmark} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTestsTab = () => {
    console.log('renderTestsTab called:', {
      activeTab,
      mockTestsCount: mockTests.length,
      mockTestsLoading,
      mockTestsError,
      mockTests: mockTests
    });
    
    // Helper function to format time limit
    const formatTimeLimit = (timeLimitMin) => {
      if (!timeLimitMin) return "No time limit";
      if (timeLimitMin < 60) return `${timeLimitMin} min`;
      const hours = Math.floor(timeLimitMin / 60);
      const minutes = timeLimitMin % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    };

    // Helper function to check if test is new (created within last 7 days)
    const isTestNew = (createdAt) => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(createdAt) > sevenDaysAgo;
    };

    // Sort mock tests by order, then by title
    const sortedMockTests = [...mockTests].sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.title.localeCompare(b.title);
    });

    // Loading state
    if (mockTestsLoading) {
      return (
        <div 
          className={`tab-pane fade ${activeTab === 'tests' ? 'show active' : ''}`}
          id="tests-tab-pane"
          role="tabpanel"
          aria-labelledby="tests-tab"
        >
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-white">Loading mock tests...</h5>
                <p className="text-light">Please wait while we fetch the available practice tests.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (mockTestsError) {
      return (
        <div 
          className={`tab-pane fade ${activeTab === 'tests' ? 'show active' : ''}`}
          id="tests-tab-pane"
          role="tabpanel"
          aria-labelledby="tests-tab"
        >
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <FontAwesomeIcon icon={faClipboardList} size="3x" />
                </div>
                <h5 className="text-white">Error Loading Mock Tests</h5>
                <p className="text-light mb-4">{mockTestsError}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No mock tests state
    if (sortedMockTests.length === 0) {
      return (
        <div 
          className={`tab-pane fade ${activeTab === 'tests' ? 'show active' : ''}`}
          id="tests-tab-pane"
          role="tabpanel"
          aria-labelledby="tests-tab"
        >
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-secondary mb-3">
                  <FontAwesomeIcon icon={faClipboardList} size="3x" />
                </div>
                <h5 className="text-white">No Mock Tests Available</h5>
                <p className="text-light">There are currently no practice tests available for this subject. Please check back later.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div 
        className={`tab-pane fade ${activeTab === 'tests' ? 'show active' : ''}`}
        id="tests-tab-pane"
        role="tabpanel"
        aria-labelledby="tests-tab"
      >
        {/* Header Section */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="text-white mb-2">Practice Tests</h4>
                <p className="text-light mb-0">{sortedMockTests.length} mock tests available • Test your knowledge</p>
              </div>
              <div className="d-flex gap-3">
                <div className="text-center">
                  <div className="text-primary h5 mb-1">{sortedMockTests.length}</div>
                  <small className="text-light">Total Tests</small>
                </div>
                <div className="text-center">
                  <div className="text-success h5 mb-1">{sortedMockTests.reduce((sum, test) => sum + (test.totalMarks || 0), 0)}</div>
                  <small className="text-light">Total Marks</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mock Tests Grid */}
        <div className="row g-4">
          {sortedMockTests.map((test) => {
            // Test is locked unless it's free or user has proper access
            // For mock test PDFs, check test access (canAccessTests) instead of material access
            const hasMockTestAccess = mockTestPurchaseStatus?.hasPurchased || mockTestContextData?.isFree || !mockTestContextData?.isPurchasable;
            const hasExamAccess = accessInfo?.hasAccess && accessInfo?.accessLevel?.canAccessTests;
            const hasAccess = isMockTestContext ? hasMockTestAccess : hasExamAccess;
            const isLocked = !test.isFree && !hasAccess;
            
            return (
              <div key={test._id} className="col-lg-4 col-md-6">
                <div className={`test-card-modern position-relative bg-dark bg-opacity-50 rounded-3 overflow-hidden border border-secondary border-opacity-25 ${isLocked ? 'locked-content' : ''}`}>
                  <div className="test-header p-4 bg-gradient-primary bg-opacity-10">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="test-icon">
                        <FontAwesomeIcon icon={faFileAlt} size="2x" className={`${isLocked ? 'text-secondary opacity-50' : 'text-primary'}`} />
                      </div>
                      <div className="d-flex flex-column align-items-end gap-2">
                        {test.isFree && (
                          <span className="badge bg-gradient bg-success px-2 py-1">
                            <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                            Free
                          </span>
                        )}
                        {isTestNew(test.createdAt) && !isLocked && !test.isFree && (
                          <span className="badge bg-gradient bg-success px-2 py-1">New</span>
                        )}
                        {isLocked && (
                          <span className="badge bg-warning text-dark px-2 py-1">
                            <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                            Access Required
                          </span>
                        )}
                      </div>
                    </div>
                  
                  <h5 className={`mb-2 line-height-relaxed ${isLocked ? 'text-secondary opacity-75' : 'text-white'}`}>
                    {test.title}
                  </h5>
                  
                  <div className="test-meta d-flex flex-wrap gap-3 mb-3">
                    {test.totalMarks && (
                      <div className={`d-flex align-items-center ${isLocked ? 'text-secondary' : 'text-light'}`}>
                        <FontAwesomeIcon icon={faClipboardList} className={`me-2 opacity-75 ${isLocked ? 'text-secondary' : 'text-warning'}`} size="sm" />
                        <small>{test.totalMarks} marks</small>
                      </div>
                    )}
                    {test.timeLimitMin && (
                      <div className={`d-flex align-items-center ${isLocked ? 'text-secondary' : 'text-light'}`}>
                        <FontAwesomeIcon icon={faClock} className={`me-2 opacity-75 ${isLocked ? 'text-secondary' : 'text-info'}`} size="sm" />
                        <small>{formatTimeLimit(test.timeLimitMin)}</small>
                      </div>
                    )}
                    <div className={`d-flex align-items-center ${isLocked ? 'text-secondary' : 'text-light'}`}>
                      <FontAwesomeIcon icon={faCalendar} className="me-2 text-secondary opacity-75" size="sm" />
                      <small>{new Date(test.createdAt).toLocaleDateString()}</small>
                    </div>
                  </div>
                </div>
                
                <div className="test-actions p-4">
                  <div className="d-flex gap-2">
                    <button 
                      className={`btn flex-grow-1 py-2 ${isLocked ? 'btn-secondary' : (test.isFree ? 'btn-success' : 'btn-light')}`}
                      onClick={() => {
                        if (isLocked) {
                          alert('Test access restricted. Please purchase the exam to access tests.');
                          return;
                        }
                        // Use the PDF viewer for mock test PDFs
                        handleViewDocument({
                          ...test,
                          _id: test._id
                        }, 'pdf');
                      }}
                      disabled={isLocked}
                    >
                      <FontAwesomeIcon icon={isLocked ? faClipboardList : faEye} className="me-2" />
                      {isLocked ? 'Locked' : (test.isFree ? 'Free to View' : 'View Test')}
                    </button>
                    <button className="btn btn-light px-3" disabled={isLocked}>
                      <FontAwesomeIcon icon={faBookmark} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPdfsTab = () => {
    const isPdfNew = (createdAt) => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(createdAt) > sevenDaysAgo;
    };

    const hasMaterialAccess = materialPurchaseStatus?.hasPurchased || materialContextData?.isFree || !materialContextData?.isPurchasable;
    const hasMockTestAccess = mockTestPurchaseStatus?.hasPurchased || mockTestContextData?.isFree || !mockTestContextData?.isPurchasable;
    const hasExamAccess = accessInfo?.hasAccess && accessInfo?.accessLevel?.canAccessMaterials;
    const hasPdfAccess = isMockTestContext ? hasMockTestAccess : (isMaterialContext ? hasMaterialAccess : hasExamAccess);

    const sortedPdfs = [...pdfs].sort((a, b) => {
      if ((a.order ?? 0) !== (b.order ?? 0)) return (a.order ?? 0) - (b.order ?? 0);
      return (a.title || '').localeCompare(b.title || '');
    });

    if (pdfsLoading) {
      return (
        <div className={`tab-pane fade ${activeTab === 'pdfs' ? 'show active' : ''}`}>
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-white">{isMockTestContext ? 'Loading mock tests...' : 'Loading materials...'}</h5>
                <p className="text-light">{isMockTestContext ? 'Please wait while we fetch the available mock tests.' : 'Please wait while we fetch the available materials.'}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (pdfsError) {
      return (
        <div className={`tab-pane fade ${activeTab === 'pdfs' ? 'show active' : ''}`}>
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <FontAwesomeIcon icon={isMockTestContext ? faClipboardList : faFilePdf} size="3x" />
                </div>
                <h5 className="text-white">{isMockTestContext ? 'Error Loading Mock Tests' : 'Error Loading Materials'}</h5>
                <p className="text-light mb-4">{pdfsError}</p>
                <button className="btn btn-primary" onClick={() => window.location.reload()}>Try Again</button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sortedPdfs.length === 0) {
      return (
        <div className={`tab-pane fade ${activeTab === 'pdfs' ? 'show active' : ''}`}>
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-secondary mb-3">
                  <FontAwesomeIcon icon={isMockTestContext ? faClipboardList : faFilePdf} size="3x" />
                </div>
                <h5 className="text-white">{isMockTestContext ? 'No Mock Tests Available' : 'No Materials Available'}</h5>
                <p className="text-light">{isMockTestContext ? 'There are no mock test papers available for this subject yet.' : 'There are no materials available for this subject yet.'}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const pdfsTabTitle = isMockTestContext ? 'Mock Tests' : 'Materials';
    const pdfsTabSubtitle = isMockTestContext
      ? `${sortedPdfs.length} mock test${sortedPdfs.length !== 1 ? 's' : ''} available`
      : `${sortedPdfs.length} material${sortedPdfs.length !== 1 ? 's' : ''} available`;

    return (
      <div className={`tab-pane fade ${activeTab === 'pdfs' ? 'show active' : ''}`}>
        <div className="row mb-5">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="text-white mb-2">{pdfsTabTitle}</h4>
                <p className="text-light mb-0">{pdfsTabSubtitle}</p>
              </div>
              <div className="d-flex gap-3">
                <div className="text-center">
                  <div className="text-primary h5 mb-1">{sortedPdfs.length}</div>
                  <small className="text-light">{isMockTestContext ? 'Total Tests' : 'Total Materials'}</small>
                </div>
                <div className="text-center">
                  <div className="text-success h5 mb-1">{sortedPdfs.filter(p => isPdfNew(p.createdAt)).length}</div>
                  <small className="text-light">New This Week</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {sortedPdfs.map((pdf) => {
            const isLocked = !pdf.isFree && !hasPdfAccess;
            return (
              <div key={pdf._id} className="col-lg-4 col-md-6">
                <div className={`material-card-modern position-relative bg-dark bg-opacity-50 rounded-3 overflow-hidden border border-secondary border-opacity-25 h-100 ${isLocked ? 'locked-content' : ''}`}>
                  <div className="material-header p-4 bg-gradient-info bg-opacity-10">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="material-icon">
                        <FontAwesomeIcon icon={faFilePdf} size="2x" className={`text-info ${isLocked ? 'opacity-50' : ''}`} />
                      </div>
                      <div className="d-flex flex-column align-items-end gap-2">
                        {pdf.isFree && (
                          <span className="badge bg-gradient bg-info px-2 py-1">
                            <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                            Free
                          </span>
                        )}
                        {isPdfNew(pdf.createdAt) && !pdf.isFree && (
                          <span className="badge bg-gradient bg-success px-2 py-1">New</span>
                        )}
                        {isLocked && (
                          <span className="badge bg-warning text-dark px-2 py-1">
                            <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                            {isMockTestContext ? 'Purchase Mock Test Package' : (isMaterialContext ? 'Purchase Material' : 'Purchase Required')}
                          </span>
                        )}
                      </div>
                    </div>
                    <h6 className="text-white mb-2 line-height-relaxed">{pdf.title}</h6>
                    {pdf.description && (
                      <p className="text-light mb-3 small opacity-75" style={{ fontSize: '0.875rem' }}>
                        {pdf.description.length > 80 ? `${pdf.description.substring(0, 80)}...` : pdf.description}
                      </p>
                    )}
                    <div className="material-meta d-flex flex-wrap gap-3 mb-3">
                      {isMockTestContext && (pdf.totalMarks != null || pdf.timeLimitMin != null) && (
                        <>
                          {pdf.totalMarks != null && (
                            <div className="d-flex align-items-center text-light">
                              <FontAwesomeIcon icon={faClipboardList} className="me-2 text-warning opacity-75" size="sm" />
                              <small>{pdf.totalMarks} marks</small>
                            </div>
                          )}
                          {pdf.timeLimitMin != null && (
                            <div className="d-flex align-items-center text-light">
                              <FontAwesomeIcon icon={faClock} className="me-2 text-info opacity-75" size="sm" />
                              <small>{pdf.timeLimitMin} min</small>
                            </div>
                          )}
                        </>
                      )}
                      <div className="d-flex align-items-center text-light">
                        <FontAwesomeIcon icon={faFilePdf} className="me-2 text-info opacity-75" size="sm" />
                        <small>PDF</small>
                      </div>
                      <div className="d-flex align-items-center text-light">
                        <FontAwesomeIcon icon={faCalendar} className="me-2 text-secondary opacity-75" size="sm" />
                        <small>{new Date(pdf.createdAt).toLocaleDateString()}</small>
                      </div>
                    </div>
                  </div>
                  <div className="material-actions p-4">
                    <div className="d-flex gap-2">
                      <button
                        className={`btn ${isLocked ? 'btn-secondary' : (pdf.isFree ? 'btn-info' : 'btn-light')} flex-grow-1 py-2`}
                        onClick={() => handleViewDocument({ ...pdf, _id: pdf._id }, 'pdf')}
                        disabled={isLocked}
                      >
                        <FontAwesomeIcon icon={isLocked ? faClipboardList : faEye} className="me-2" />
                        {isLocked ? 'Locked' : (pdf.isFree ? 'Free to View' : 'View')}
                      </button>
                      <button className="btn btn-light px-3">
                        <FontAwesomeIcon icon={faBookmark} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <div className="container-fluid px-4">
        {/* Back button when in mock test context (same pattern as materials) */}
        {isMockTestContext && mockTestCode && (
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-link text-light text-decoration-none p-0"
              onClick={() => navigate(`/mock-tests/${mockTestCode}`)}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Mock Test Package
            </button>
          </div>
        )}

        {/* Hero Header */}
        <div className="subject-hero mb-1">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="hero-content">
                <h2 className="text-white mb-3 display-6">
                  {subjectData.title}
                </h2>
                <p className="text-light fs-5 mb-4 opacity-75">
                  {subjectData.description || (isMockTestContext ? 'Mock test papers for this subject.' : 'Comprehensive video lectures and study materials for this subject.')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Access Level Banner (exam context only; not for material or mock test context) */}
        {!isMaterialContext && !isMockTestContext && accessInfo && (
          <div className="row mb-4">
            <div className="col-12">
              {accessInfo.hasAccess ? (
                <>
                  {accessInfo.accessLevel?.accessType === 'tests' ? (
                    <div className="alert alert-warning d-flex align-items-center" role="alert">
                      <FontAwesomeIcon icon={faClipboardList} className="me-3 fs-4" />
                      <div className="flex-grow-1">
                        <h6 className="alert-heading mb-1">Tests-Only Access</h6>
                        <p className="mb-2">You currently have access to tests and mock exams only. Upgrade to full access to unlock:</p>
                        <ul className="mb-3 small">
                          <li>📹 All video lectures</li>
                          <li>📚 Study materials and notes</li>
                          <li>✨ Complete learning experience</li>
                        </ul>
                        <button className="btn btn-primary btn-sm">
                          <FontAwesomeIcon icon={faPlay} className="me-2" />
                          Upgrade to Full Access
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-success d-flex align-items-center" role="alert">
                      <FontAwesomeIcon icon={faPlay} className="me-3 fs-4" />
                      <div>
                        <h6 className="alert-heading mb-1">Full Access Granted</h6>
                        <p className="mb-0">You have full access to all content including videos, materials, and tests.</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                  <FontAwesomeIcon icon={faClipboardList} className="me-3 fs-4" />
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-1">
                      {accessInfo.isExpired ? 'Subscription Ended' : 'Access Required'}
                    </h6>
                    <p className="mb-2">
                      {accessInfo.isExpired ? (accessInfo.message || RENEWAL_MESSAGE) : 'Purchase this exam to access all content.'}
                    </p>
                    <button className="btn btn-primary btn-sm">
                      <FontAwesomeIcon icon={faPlay} className="me-2" />
                      {accessInfo.isExpired ? 'Renew Access' : 'Purchase Exam'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Tabs: only PDFs when under material or mock test, else Videos / Tests / PDFs */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="nav-tabs-modern">
              <ul className="nav nav-pills nav-pills-modern" role="tablist">
                {!isMaterialContext && !isMockTestContext && (
                  <>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'videos' ? 'active' : ''}`}
                        onClick={() => {
                          console.log('Videos tab clicked');
                          setActiveTab('videos');
                        }}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'videos'}
                        aria-controls="videos-tab-pane"
                        id="videos-tab"
                      >
                        <FontAwesomeIcon icon={faVideo} className="me-2" />
                        Videos
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'tests' ? 'active' : ''}`}
                        onClick={() => {
                          console.log('Tests tab clicked, setting activeTab to tests');
                          setActiveTab('tests');
                        }}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'tests'}
                        aria-controls="tests-tab-pane"
                        id="tests-tab"
                      >
                        <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                        Tests
                      </button>
                    </li>
                  </>
                )}
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'pdfs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pdfs')}
                    type="button"
                    role="tab"
                  >
                    <FontAwesomeIcon icon={isMockTestContext ? faClipboardList : faFilePdf} className="me-2" />
                    {isMockTestContext ? 'Mock Tests' : 'Materials'}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {!isMaterialContext && !isMockTestContext && renderVideosTab()}
          {!isMaterialContext && !isMockTestContext && renderTestsTab()}
          {renderPdfsTab()}
        </div>
      </div>

      {/* Video Player Modal */}
      {showVideoPlayer && currentVideo && (
        <VideoPlayer
          video={currentVideo}
          onClose={handleCloseVideoPlayer}
        />
      )}

      {/* Document Viewer Modal */}
      {showDocumentViewer && currentDocument && (
        <SimpleDocumentViewer
          isOpen={showDocumentViewer}
          onClose={handleCloseDocumentViewer}
          documentUrl={currentDocument.url}
          documentTitle={currentDocument.title}
          documentType={currentDocument.type}
        />
      )}

      {/* PDF Viewer Modal */}
      {showPDFViewer && currentPDF && (
        <PDFViewer
          isOpen={showPDFViewer}
          onClose={handleClosePDFViewer}
          documentUrl={currentPDF.url}
          documentTitle={currentPDF.title}
        />
      )}
    </div>
  );
};

export default SubjectPage; 